import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, forkJoin, of } from 'rxjs';
import { PunchoutCustomerConfig } from '../../../../models/punchout';
import { GungModalService } from '../../../../services/gung-modal/gung-modal.service';
import { PunchoutConfigAdminService } from '../../../../services/punchout/punchout-config-admin.service';
import { UsersService } from '../../../../services/users/users.service';
import { User } from '../../../../state/auth/types';
import { connectedPunchoutUsers, unconnectedPunchoutUsers } from '../../../../utils/punchout-user-utils';

/** The punchout customer configurations tab: CRUD over PunchoutCustomerConfig. */
@Component({
  selector: 'lib-gung-settings-punchout-configs',
  templateUrl: './gung-settings-punchout-configs.component.html',
  styleUrl: './gung-settings-punchout-configs.component.scss',
  standalone: false
})
export class GungSettingsPunchoutConfigsComponent implements OnInit {
  protected adminService = inject(PunchoutConfigAdminService);
  protected modalService = inject(GungModalService);
  protected translateService = inject(TranslateService);
  protected usersService = inject(UsersService);

  configs: PunchoutCustomerConfig[] = [];
  loading = false;

  /**
   * False when the user list could not be read at all — most often because the admin lacks the
   * rights for json/users. Every user-derived surface is then omitted rather than rendered empty:
   * a zero count would assert "no users are configured", which is a wrong answer rather than a
   * missing one. There is deliberately no message; the failing request is visible in the network
   * tab.
   */
  usersAvailable = false;
  users: User[] = [];
  usersByConfigId = new Map<string, User[]>();
  unconnectedUsers: User[] = [];
  unconnectedExpanded = false;

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    // Both legs resolve together so the users column never appears a beat after the table. Only the
    // users leg is caught: a failure there must not take down config CRUD, which works without it.
    forkJoin({
      configs: this.adminService.list().pipe(first()),
      users: this.usersService.getAllUsers().pipe(
        first(),
        catchError(() => of(null))
      )
    }).subscribe({
      next: ({ configs, users }) => {
        this.configs = configs ?? [];
        this.setUsers(users);
        this.loading = false;
      },
      error: () => {
        this.configs = [];
        this.setUsers(null);
        this.loading = false;
      }
    });
  }

  private setUsers(users: User[] | null): void {
    this.usersAvailable = !!users;
    this.users = users ?? [];
    this.unconnectedExpanded = false;
    this.usersByConfigId = new Map(
      this.configs.map(config => [config.id, connectedPunchoutUsers(this.users, config.id)])
    );
    this.unconnectedUsers = users
      ? unconnectedPunchoutUsers(
          this.users,
          this.configs.map(config => config.id)
        )
      : [];
  }

  connectedCount(config: PunchoutCustomerConfig): number {
    return this.usersByConfigId.get(config.id)?.length ?? 0;
  }

  add(): void {
    this.openEditModal(undefined);
  }

  edit(config: PunchoutCustomerConfig): void {
    // undefined rather than an empty array when the user list is unavailable, so the modal can tell
    // "rights missing" from "genuinely nobody connected" without a second flag.
    this.openEditModal(config, this.usersAvailable ? (this.usersByConfigId.get(config.id) ?? []) : undefined);
  }

  /**
   * Open the edit modal pre-filled from an existing config, as an unsaved new one. Nothing is
   * written until the admin saves: the backend mints a fresh UUID because the id is blank
   * (PunchoutCustomerConfigService.save), so the source config is never touched.
   *
   * Order intake credentials are deliberately not carried over. They authenticate one specific
   * buyer, and intake fails closed on a blank secret, so a copy that is saved unedited rejects
   * incoming orders rather than accepting the source buyer's credentials.
   */
  copy(config: PunchoutCustomerConfig): void {
    const { id, createdDate, updatedDate, orderSharedSecret, orderBasicAuthUsername, orderBasicAuthPassword, ...rest } =
      config;
    this.openEditModal({
      ...rest,
      id: undefined,
      name: this.translateService.instant('PUNCHOUT_COPY_OF', { value: config.name })
    } as unknown as PunchoutCustomerConfig);
  }

  delete(config: PunchoutCustomerConfig): void {
    this.modalService
      .openConfirmYesNoModal('CONFIRM_DELETE', 'CONFIRM_DELETE_PUNCHOUT_CONFIG', { size: 'sm' })
      .then(ok => {
        if (!ok) {
          return;
        }
        this.adminService
          .delete(config.id)
          .pipe(first())
          .subscribe(() => this.refresh());
      });
  }

  private openEditModal(initial?: PunchoutCustomerConfig, connectedUsers?: User[]): void {
    this.modalService
      .openEditPunchoutConfig(initial, undefined, connectedUsers)
      .then(saved => {
        if (saved) {
          this.refresh();
        }
      })
      .catch(() => undefined);
  }
}
